import { useEffect, useState } from "react";
import { api, formatTZS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Edit2, Trash2, CheckCircle2, Circle } from "lucide-react";

const CATEGORIES = [
  { value: "restaurant", label: "Restaurant" },
  { value: "hotel_room", label: "Hotel Room" },
  { value: "maintenance", label: "Maintenance" },
  { value: "utilities", label: "Utilities" },
  { value: "salaries", label: "Salaries" },
  { value: "other", label: "Other" },
];

const catBadge = {
  restaurant: "bg-orange-100 text-orange-700",
  hotel_room: "bg-blue-100 text-blue-700",
  maintenance: "bg-amber-100 text-amber-700",
  utilities: "bg-purple-100 text-purple-700",
  salaries: "bg-emerald-100 text-emerald-700",
  other: "bg-slate-100 text-slate-700",
};

const EMPTY = {
  category: "restaurant", item_name: "", amount: 0,
  status: "unpaid", vendor: "", notes: "",
  incurred_on: new Date().toISOString().slice(0, 10),
};

export default function ExpensesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const canDelete = user?.role === "super_admin" || user?.role === "admin";

  const load = async () => {
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const [r, s] = await Promise.all([
      api.get(`/admin/expenses?${params.toString()}`),
      api.get("/admin/expenses/summary"),
    ]);
    setRows(r.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, [category, statusFilter]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({
    category: r.category, item_name: r.item_name, amount: r.amount, status: r.status,
    vendor: r.vendor || "", notes: r.notes || "", incurred_on: r.incurred_on,
  }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) await api.put(`/admin/expenses/${editing.id}`, payload);
      else await api.post("/admin/expenses", payload);
      toast.success(editing ? "Expense updated" : "Expense logged");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this expense?")) return;
    try { await api.delete(`/admin/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePaid = async (id) => {
    try { await api.post(`/admin/expenses/${id}/toggle-paid`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPaid = summary.reduce((a, s) => a + (s.paid || 0), 0);
  const totalUnpaid = summary.reduce((a, s) => a + (s.unpaid || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Expenses</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Log restaurant, hotel room, maintenance, utilities & other costs.</p>
        </div>
        <Button onClick={openNew} data-testid="new-expense-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> Log expense</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-emerald-200"><CardContent className="p-5"><p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Paid</p><p className="mt-2 font-heading text-2xl text-emerald-700" data-testid="exp-paid-total">{formatTZS(totalPaid)}</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-5"><p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Unpaid</p><p className="mt-2 font-heading text-2xl text-red-700" data-testid="exp-unpaid-total">{formatTZS(totalUnpaid)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Total logged</p><p className="mt-2 font-heading text-2xl text-[color:var(--deep)]">{formatTZS(totalPaid + totalUnpaid)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Entries</p><p className="mt-2 font-heading text-2xl text-[color:var(--deep)]">{rows.length}</p></CardContent></Card>
      </div>

      {summary.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">By category</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {summary.map((s) => (
                <div key={s._id} className={`p-3 rounded-xl ${catBadge[s._id] || "bg-slate-100"}`}>
                  <p className="text-xs uppercase tracking-wider opacity-80">{CATEGORIES.find((c) => c.value === s._id)?.label || s._id}</p>
                  <p className="font-heading text-lg mt-1">{formatTZS(s.total)}</p>
                  <p className="text-xs opacity-70">{s.count} entries · {formatTZS(s.unpaid)} unpaid</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3 flex-wrap">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48" data-testid="exp-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="exp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                  <TableCell className="text-sm">{r.incurred_on}</TableCell>
                  <TableCell><Badge className={`${catBadge[r.category]} hover:opacity-90`}>{CATEGORIES.find((c) => c.value === r.category)?.label || r.category}</Badge></TableCell>
                  <TableCell className="text-sm">
                    <p className="font-medium">{r.item_name}</p>
                    {r.notes && <p className="text-xs text-[color:var(--muted-text)]">{r.notes}</p>}
                  </TableCell>
                  <TableCell className="text-sm">{r.vendor || "—"}</TableCell>
                  <TableCell className="font-semibold text-sm">{formatTZS(r.amount)}</TableCell>
                  <TableCell>
                    <button onClick={() => togglePaid(r.id)} data-testid={`toggle-paid-${r.id}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${r.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {r.status === "paid" ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />} {r.status}
                    </button>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-exp-${r.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                    {canDelete && <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-exp-${r.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[color:var(--muted-text)]">No expenses logged yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit expense" : "Log expense"}</DialogTitle>
            <DialogDescription>Record a cost that affects hotel P&L.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.incurred_on} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} data-testid="exp-date" /></div>
            <div className="col-span-2"><Label>Item</Label><Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} data-testid="exp-item" placeholder="e.g. Cooking gas refill, Bedsheets, Electric bill" /></div>
            <div><Label>Amount (TZS)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="exp-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Vendor (optional)</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="exp-vendor" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="exp-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-exp-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">{editing ? "Save" : "Log expense"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
